import React, { useState, useEffect, useCallback } from 'react';
import './TeacherDissertation.css';
import collegeConfig from '../../config/collegeConfig';
import { saveFile } from '../../utils/saveFile';
import {
  FaExclamationTriangle, FaCheckCircle, FaSave, FaChevronDown,
  FaChevronUp, FaGraduationCap, FaEdit, FaDownload, FaFilePdf, FaFileImage
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

// Fixed 6-month buckets used by dissertations submitted before the flexible
// progress-report system existed. Nothing creates new entries in this format
// anymore — it's kept only to display/grade whatever old data already exists.
const PERIODS_LEGACY = ['0-6', '6-12', '12-18', '18-24', '24-30', '30-36'];
const PERIOD_LABELS_LEGACY = {
  '0-6': 'Month 0–6', '6-12': 'Month 6–12', '12-18': 'Month 12–18',
  '18-24': 'Month 18–24', '24-30': 'Month 24–30', '30-36': 'Month 30–36'
};
const GRADE_LABELS = { 4: 'Excellent', 3: 'Good', 2: 'Satisfactory', 1: 'Needs Improvement', 0: 'Unsatisfactory' };
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatFlexReport = (r) => `${(r.months || []).map(m => MONTH_NAMES[m - 1]).join(', ')} ${r.year}`;

const OBSERVATION_CRITERIA = [
  'Periodic consultation with P.G. Teacher / Assistant P.G. Teacher',
  'Regular collection of case / clinical material / data',
  'Regular collection and review of literature',
  'Depth of analysis / discussion',
  'Departmental Presentation of findings',
  'Quality of final output',
  'Others'
];

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

export default function TeacherDissertation({ user, assignedCourse, allStudents, myStudentIds }) {
  const [selectedBatch, setSelectedBatch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [dissertation, setDissertation] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [studentsWithStatus, setStudentsWithStatus] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [gradeInputs, setGradeInputs] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [docDownloadingId, setDocDownloadingId] = useState(null);

  const token = () => localStorage.getItem('token');

  const PERIODS = PERIODS_LEGACY;
  const PERIOD_LABELS = PERIOD_LABELS_LEGACY;

  // Fetch students with dissertation status
  useEffect(() => {
    if (!assignedCourse) return;
    const fetchList = async () => {
      setLoadingList(true);
      try {
        const res = await fetch(`${API}/dissertation/students?courseId=${assignedCourse._id}`, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        const data = await res.json();
        if (data.success) setStudentsWithStatus(data.students || []);
      } catch { /* silently use allStudents fallback */ }
      setLoadingList(false);
    };
    fetchList();
  }, [assignedCourse]);

  // Build merged list: use studentsWithStatus if loaded, otherwise fall back to allStudents
  const baseStudents = studentsWithStatus.length > 0 ? studentsWithStatus : (allStudents || []);

  // Unique batches
  const batches = Array.from(
    new Set(baseStudents.map(s => s.batch).filter(Boolean))
  ).sort();

  // Filter students — batch must be selected first
  const filteredStudents = baseStudents.filter(s => {
    const id = String(s._id || s.id);
    const inMyStudents = !myStudentIds || myStudentIds.length === 0 || myStudentIds.includes(id);
    const inBatch = selectedBatch === '' ? false : (selectedBatch === 'all' || s.batch === selectedBatch);
    const name = `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim().toLowerCase();
    const reg = (s.profile?.registrationNumber || '').toLowerCase();
    const query = searchTerm.trim().toLowerCase();
    const matchesSearch = !query || name.includes(query) || reg.includes(query);
    return inMyStudents && inBatch && matchesSearch;
  }).sort((a, b) => {
    const na = `${a.profile?.firstName || ''} ${a.profile?.lastName || ''}`.trim().toLowerCase();
    const nb = `${b.profile?.firstName || ''} ${b.profile?.lastName || ''}`.trim().toLowerCase();
    return na.localeCompare(nb);
  });

  const fetchStudentDissertation = useCallback(async (studentId) => {
    if (!assignedCourse) return;
    setLoadingStudent(true);
    setError('');
    setSuccess('');
    setExpandedPeriods({});
    setGradeInputs({});
    setCommentInputs({});
    try {
      const res = await fetch(
        `${API}/dissertation/student/${studentId}?courseId=${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation || null);
        setStudentData(data.student || null);
        // Pre-fill existing grades (both legacy fixed-period and flexible reports)
        const gi = {};
        const ci = {};
        (data.dissertation?.reports || []).forEach(r => {
          if (r.isGraded) {
            gi[r.period] = String(r.grade);
            ci[r.period] = r.teacherComments || '';
          }
        });
        (data.dissertation?.flexibleReports || []).forEach(r => {
          if (r.isGraded) {
            gi[r._id] = String(r.grade);
            ci[r._id] = r.teacherComments || '';
          }
        });
        setGradeInputs(gi);
        setCommentInputs(ci);
      } else {
        setError(data.message || 'Failed to load student data.');
      }
    } catch {
      setError('Failed to load student data. Please try again.');
    } finally {
      setLoadingStudent(false);
    }
  }, [assignedCourse]);

  const handleSelectStudent = (studentId) => {
    setSelectedStudentId(studentId);
    fetchStudentDissertation(studentId);
  };

  const handleGradeSubmit = async (period) => {
    setError(''); setSuccess('');
    const gradeVal = gradeInputs[period];
    if (gradeVal === undefined || gradeVal === '') {
      setError(`Please select a grade for ${PERIOD_LABELS[period]}.`);
      return;
    }
    setSaving(period);
    try {
      const res = await fetch(`${API}/dissertation/grade`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId: selectedStudentId,
          courseId: assignedCourse._id,
          period,
          grade: Number(gradeVal),
          teacherComments: commentInputs[period] || ''
        })
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        // Refresh student list to update badges
        if (assignedCourse) {
          fetch(`${API}/dissertation/students?courseId=${assignedCourse._id}`, {
            headers: { Authorization: `Bearer ${token()}` }
          }).then(r => r.json()).then(d => { if (d.success) setStudentsWithStatus(d.students || []); }).catch(() => {});
        }
        setSuccess(`${PERIOD_LABELS[period]} graded successfully.`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save grade.');
      }
    } catch {
      setError('Failed to save grade. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const togglePeriod = (period) => {
    setExpandedPeriods(p => ({ ...p, [period]: !p[period] }));
  };

  const handleFlexGradeSubmit = async (reportId, label) => {
    setError(''); setSuccess('');
    const gradeVal = gradeInputs[reportId];
    if (gradeVal === undefined || gradeVal === '') {
      setError(`Please select a grade for ${label}.`);
      return;
    }
    setSaving(reportId);
    try {
      const res = await fetch(`${API}/dissertation/flexible-report/${reportId}/grade`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId: selectedStudentId,
          courseId: assignedCourse._id,
          grade: Number(gradeVal),
          teacherComments: commentInputs[reportId] || ''
        })
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        if (assignedCourse) {
          fetch(`${API}/dissertation/students?courseId=${assignedCourse._id}`, {
            headers: { Authorization: `Bearer ${token()}` }
          }).then(r => r.json()).then(d => { if (d.success) setStudentsWithStatus(d.students || []); }).catch(() => {});
        }
        setSuccess(`${label} graded successfully.`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save grade.');
      }
    } catch {
      setError('Failed to save grade. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const downloadIec = async () => {
    setDocDownloadingId('iec');
    try {
      const res = await fetch(
        `${API}/dissertation/student/${selectedStudentId}/iec-certificate/download?courseId=${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const data = await res.json();
      if (data.success) {
        await saveFile(data.data, data.mimeType, data.fileName || 'iec-certificate');
      } else {
        setError(data.message || 'Download failed.');
      }
    } catch {
      setError('Download failed.');
    } finally {
      setDocDownloadingId(null);
    }
  };

  const downloadDocument = async (docId) => {
    setDocDownloadingId(docId);
    try {
      const res = await fetch(
        `${API}/dissertation/student/${selectedStudentId}/documents/${docId}/download?courseId=${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const data = await res.json();
      if (data.success) {
        await saveFile(data.data, data.mimeType, data.fileName || 'document');
      } else {
        setError(data.message || 'Download failed.');
      }
    } catch {
      setError('Download failed.');
    } finally {
      setDocDownloadingId(null);
    }
  };

  const getReport = (period) => dissertation?.reports?.find(r => r.period === period);

  const getStudentBadge = (s) => {
    const diss = s.dissertation;
    if (!diss) return { cls: 'none', label: 'No Entry' };
    if (diss.isCompleted) return { cls: 'graded', label: 'Graded' };
    if (diss.topicName && diss.topicName !== 'Pending submission by student') return { cls: 'submitted', label: 'Submitted' };
    return { cls: 'none', label: 'No Entry' };
  };

  // When switching batch, reset selection and search
  useEffect(() => {
    setSelectedStudentId(null);
    setDissertation(null);
    setStudentData(null);
    setSearchTerm('');
  }, [selectedBatch]);

  const selectedStudent = baseStudents.find(s => String(s._id || s.id) === String(selectedStudentId));

  return (
    <div className="tcdiss-container">
      {/* Observation Criteria */}
      <div className="tcdiss-criteria-card">
        <h4><FaExclamationTriangle /> Grading Observation Criteria</h4>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#856404' }}>
          Assess the student's periodic progress report based on the following criteria:
        </p>
        <ol>
          {OBSERVATION_CRITERIA.map((c, i) => <li key={i}>{c}</li>)}
        </ol>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#856404', fontStyle: 'italic' }}>
          Grade each progress report out of 4: 0 = Unsatisfactory, 1 = Needs Improvement, 2 = Satisfactory, 3 = Good, 4 = Excellent
        </p>
      </div>

      {/* Student Selection */}
      <div className="tcdiss-card">
        <div className="tcdiss-card-title">Select Student</div>

        <div className="tcdiss-selector-row">
          <div className="tcdiss-selector-group">
            <label className="tcdiss-label">Step 1 — Select Batch</label>
            <select
              className="tcdiss-select"
              value={selectedBatch}
              onChange={e => setSelectedBatch(e.target.value)}
            >
              <option value="">— Choose a batch —</option>
              <option value="all">All Batches</option>
              {batches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {!selectedBatch ? (
          <p style={{ color: '#bbb', fontSize: 13, margin: '4px 0 0', fontStyle: 'italic' }}>
            Please select a batch to see students.
          </p>
        ) : (
          <>
            <div className="tcdiss-search-row">
              <div className="tcdiss-search-box">
                <span className="tcdiss-search-icon">🔍</span>
                <input
                  className="tcdiss-search-input"
                  type="text"
                  placeholder="Search by name or registration number..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="tcdiss-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                )}
              </div>
            </div>

            {filteredStudents.length === 0 ? (
              <p style={{ color: '#aaa', fontSize: 14, margin: '8px 0 0' }}>
                {searchTerm ? 'No students match your search.' : 'No students found in this batch.'}
              </p>
            ) : (
              <div className="tcdiss-students-list">
                {filteredStudents.map(s => {
                  const id = String(s._id || s.id);
                  const name = `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim() || 'Unknown';
                  const initials = `${(s.profile?.firstName || '').charAt(0)}${(s.profile?.lastName || '').charAt(0)}`.toUpperCase();
                  const badge = getStudentBadge(s);
                  return (
                    <div
                      key={id}
                      className={`tcdiss-student-item ${selectedStudentId === id ? 'active' : ''}`}
                      onClick={() => handleSelectStudent(id)}
                    >
                      <div className="tcdiss-student-avatar">{initials || '?'}</div>
                      <div className="tcdiss-student-info">
                        <p className="tcdiss-student-name">{name}</p>
                        <p className="tcdiss-student-reg">
                          {s.profile?.registrationNumber || s.email || ''}
                          {s.batch ? ` · ${s.batch}` : ''}
                        </p>
                      </div>
                      <span className={`tcdiss-diss-badge ${badge.cls}`}>{badge.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Student Dissertation Details + Grading */}
      {selectedStudentId && (
        <>
          {error && <div className="tcdiss-alert tcdiss-alert-error">{error}</div>}
          {success && <div className="tcdiss-alert tcdiss-alert-success">{success}</div>}

          {loadingStudent ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
              <span className="tcdiss-spinner" /> Loading...
            </div>
          ) : (
            <div className="tcdiss-card">
              <div className="tcdiss-card-title" style={{ marginBottom: 20 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaGraduationCap color="#667eea" />
                  {`${selectedStudent?.profile?.firstName || ''} ${selectedStudent?.profile?.lastName || ''}`.trim() || 'Student'}'s Dissertation
                </span>
                {dissertation?.isCompleted && (
                  <span style={{ fontSize: 12, background: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>
                    <FaCheckCircle style={{ marginRight: 4 }} />Completed
                  </span>
                )}
              </div>

              {/* Dissertation details */}
              {dissertation && dissertation.topicName !== 'Pending submission by student' ? (
                <div className="tcdiss-detail-grid">
                  <div className="tcdiss-detail-item full-width">
                    <label>Topic of Study</label>
                    <p style={{ fontWeight: 600 }}>{dissertation.topicName}</p>
                  </div>
                  <div className="tcdiss-detail-item full-width">
                    <label>P.G. Teacher(s)</label>
                    <p>{dissertation.pgTeacherNames?.join(', ') || '—'}</p>
                  </div>
                  <div className="tcdiss-detail-item">
                    <label>IEC Approval Date</label>
                    <p>{fmt(dissertation.approvalDate)}</p>
                  </div>
                  <div className="tcdiss-detail-item">
                    <label>Submission Date</label>
                    <p>{fmt(dissertation.submissionDate)}</p>
                  </div>
                  <div className="tcdiss-detail-item full-width">
                    <label>IEC Approval Certificate</label>
                    {dissertation.iecCertificate?.fileName ? (
                      <button className="tcdiss-btn tcdiss-btn-secondary" style={{ marginTop: 4 }} onClick={downloadIec} disabled={docDownloadingId === 'iec'}>
                        {docDownloadingId === 'iec' ? <span className="tcdiss-spinner" /> : <FaDownload />} {dissertation.iecCertificate.fileName}
                      </button>
                    ) : <p>Not uploaded</p>}
                  </div>
                  {dissertation.documents?.length > 0 && (
                    <div className="tcdiss-detail-item full-width">
                      <label>Documents & Certificates</label>
                      {dissertation.documents.map(doc => (
                        <button
                          key={doc._id}
                          className="tcdiss-btn tcdiss-btn-secondary"
                          style={{ marginTop: 4, marginRight: 8 }}
                          onClick={() => downloadDocument(doc._id)}
                          disabled={docDownloadingId === doc._id}
                        >
                          {docDownloadingId === doc._id
                            ? <span className="tcdiss-spinner" />
                            : (doc.mimeType === 'application/pdf' ? <FaFilePdf /> : <FaFileImage />)}
                          {' '}{doc.label ? `${doc.label} — ` : ''}{doc.fileName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="tcdiss-no-submission">
                  <FaGraduationCap size={32} color="#ccc" />
                  <p style={{ marginTop: 10 }}>
                    {dissertation ? 'Student has not submitted dissertation details yet.' : 'No dissertation submitted yet.'}
                  </p>
                  <p style={{ fontSize: 12, color: '#bbb' }}>You can still grade progress reports below.</p>
                </div>
              )}

              <hr className="tcdiss-divider" />

              {/* Flexible progress reports (student-chosen months) */}
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#333', margin: '0 0 14px' }}>
                Progress Reports
              </h3>
              {(dissertation?.flexibleReports || []).length === 0 ? (
                <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 14px' }}>Student has not submitted any progress reports yet.</p>
              ) : (
                <div className="tcdiss-periods-list">
                  {[...dissertation.flexibleReports].sort((a, b) => (a.year - b.year) || (a.months[0] - b.months[0])).map(report => {
                    const isGraded = report.isGraded;
                    const isExpanded = expandedPeriods[report._id];
                    const currentGradeInput = gradeInputs[report._id];
                    const label = formatFlexReport(report);

                    return (
                      <div key={report._id} className={`tcdiss-period-card ${isGraded ? 'graded' : ''}`}>
                        <div className={`tcdiss-period-header ${isGraded ? 'graded' : ''}`} onClick={() => togglePeriod(report._id)}>
                          <span className="tcdiss-period-title">{label}</span>
                          <div className="tcdiss-period-right">
                            {isGraded && <span className="tcdiss-grade-chip">{report.grade}/4</span>}
                            <span className={`tcdiss-badge ${isGraded ? 'tcdiss-badge-graded' : report?.content ? 'tcdiss-badge-content' : 'tcdiss-badge-empty'}`}>
                              {isGraded ? 'Graded' : report?.content ? 'Has Content' : 'No Content'}
                            </span>
                            {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="tcdiss-period-body">
                            <p style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 6 }}>STUDENT'S PROGRESS REPORT</p>
                            <div className={`tcdiss-student-content ${!report?.content ? 'empty' : ''}`}>
                              {report?.content || 'Student has not submitted a progress report for this period.'}
                            </div>
                            {report?.updatedAt && (
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 14 }}>
                                Student last updated: {fmt(report.updatedAt)}
                              </p>
                            )}

                            <div className="tcdiss-grade-section">
                              <h4>
                                {isGraded ? <><FaEdit style={{ marginRight: 6 }} />Update Grade</> : 'Assign Grade'}
                              </h4>

                              {isGraded && (
                                <div className="tcdiss-existing-grade">
                                  <FaCheckCircle color="#27AE60" />
                                  <span>
                                    Current grade: <strong>{report.grade}/4</strong> — {GRADE_LABELS[report.grade]}
                                    {report.gradedByName && ` (by ${report.gradedByName})`}
                                  </span>
                                </div>
                              )}

                              <p style={{ fontSize: 12, color: '#888', margin: '0 0 10px' }}>Select grade (0–4):</p>
                              <div className="tcdiss-grade-options">
                                {[0, 1, 2, 3, 4].map(g => (
                                  <label key={g} className="tcdiss-grade-option">
                                    <input
                                      type="radio"
                                      name={`flex-grade-${report._id}`}
                                      value={String(g)}
                                      checked={currentGradeInput === String(g)}
                                      onChange={() => setGradeInputs(prev => ({ ...prev, [report._id]: String(g) }))}
                                    />
                                    <span className={`tcdiss-grade-btn ${currentGradeInput === String(g) ? 'selected' : ''}`}>
                                      <span className="grade-num">{g}</span>
                                      <span className="grade-label">{GRADE_LABELS[g]}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>

                              <textarea
                                className="tcdiss-comments-input"
                                placeholder="Optional: Comments / feedback for the student"
                                value={commentInputs[report._id] || ''}
                                onChange={e => setCommentInputs(prev => ({ ...prev, [report._id]: e.target.value }))}
                              />

                              <div className="tcdiss-btn-row">
                                <button
                                  className="tcdiss-btn tcdiss-btn-primary"
                                  onClick={() => handleFlexGradeSubmit(report._id, label)}
                                  disabled={saving === report._id || currentGradeInput === undefined || currentGradeInput === ''}
                                >
                                  {saving === report._id
                                    ? <><span className="tcdiss-spinner" /> Saving...</>
                                    : <><FaSave /> {isGraded ? 'Update Grade' : 'Save Grade'}</>
                                  }
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Previous progress reports (legacy fixed-period format) */}
              {(dissertation?.reports || []).length > 0 && (
                <>
                  <hr className="tcdiss-divider" />
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#333', margin: '0 0 14px' }}>
                    Previous Progress Reports
                  </h3>
                  <div className="tcdiss-periods-list">
                    {PERIODS.filter(period => getReport(period)).map(period => {
                      const report = getReport(period);
                      const isGraded = report?.isGraded;
                      const isExpanded = expandedPeriods[period];
                      const currentGradeInput = gradeInputs[period];

                      return (
                        <div key={period} className={`tcdiss-period-card ${isGraded ? 'graded' : ''}`}>
                          <div
                            className={`tcdiss-period-header ${isGraded ? 'graded' : ''}`}
                            onClick={() => togglePeriod(period)}
                          >
                            <span className="tcdiss-period-title">{PERIOD_LABELS[period]}</span>
                            <div className="tcdiss-period-right">
                              {isGraded && <span className="tcdiss-grade-chip">{report.grade}/4</span>}
                              <span className={`tcdiss-badge ${isGraded ? 'tcdiss-badge-graded' : report?.content ? 'tcdiss-badge-content' : 'tcdiss-badge-empty'}`}>
                                {isGraded ? 'Graded' : report?.content ? 'Has Content' : 'No Content'}
                              </span>
                              {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="tcdiss-period-body">
                              <p style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 6 }}>STUDENT'S PROGRESS REPORT</p>
                              <div className={`tcdiss-student-content ${!report?.content ? 'empty' : ''}`}>
                                {report?.content || 'Student has not submitted a progress report for this period.'}
                              </div>
                              {report?.updatedAt && (
                                <p style={{ fontSize: 11, color: '#bbb', marginBottom: 14 }}>
                                  Student last updated: {fmt(report.updatedAt)}
                                </p>
                              )}

                              <div className="tcdiss-grade-section">
                                <h4>
                                  {isGraded ? <><FaEdit style={{ marginRight: 6 }} />Update Grade</> : 'Assign Grade'}
                                </h4>

                                {isGraded && (
                                  <div className="tcdiss-existing-grade">
                                    <FaCheckCircle color="#27AE60" />
                                    <span>
                                      Current grade: <strong>{report.grade}/4</strong> — {GRADE_LABELS[report.grade]}
                                      {report.gradedByName && ` (by ${report.gradedByName})`}
                                    </span>
                                  </div>
                                )}

                                <p style={{ fontSize: 12, color: '#888', margin: '0 0 10px' }}>Select grade (0–4):</p>
                                <div className="tcdiss-grade-options">
                                  {[0, 1, 2, 3, 4].map(g => (
                                    <label key={g} className="tcdiss-grade-option">
                                      <input
                                        type="radio"
                                        name={`grade-${period}`}
                                        value={String(g)}
                                        checked={currentGradeInput === String(g)}
                                        onChange={() => setGradeInputs(prev => ({ ...prev, [period]: String(g) }))}
                                      />
                                      <span className={`tcdiss-grade-btn ${currentGradeInput === String(g) ? 'selected' : ''}`}>
                                        <span className="grade-num">{g}</span>
                                        <span className="grade-label">{GRADE_LABELS[g]}</span>
                                      </span>
                                    </label>
                                  ))}
                                </div>

                                <textarea
                                  className="tcdiss-comments-input"
                                  placeholder="Optional: Comments / feedback for the student"
                                  value={commentInputs[period] || ''}
                                  onChange={e => setCommentInputs(prev => ({ ...prev, [period]: e.target.value }))}
                                />

                                <div className="tcdiss-btn-row">
                                  <button
                                    className="tcdiss-btn tcdiss-btn-primary"
                                    onClick={() => handleGradeSubmit(period)}
                                    disabled={saving === period || currentGradeInput === undefined || currentGradeInput === ''}
                                  >
                                    {saving === period
                                      ? <><span className="tcdiss-spinner" /> Saving...</>
                                      : <><FaSave /> {isGraded ? 'Update Grade' : 'Save Grade'}</>
                                    }
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
